import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useCart } from "../context/CartContext";
import ErrorModal from "./ErrorModal";
import ExpandableText from "./ExpandableText";
import { useScrollLock } from "../hooks/useScrollLock";
import { getMenuItemImage, handleMenuImageError } from "../utils/menuImage";

const mainProteinIds = [104, 105, 106, 107, 108];

// "Drinks" category (16) and its subcategories. Food/ingredients
// inventory is on hold — only drinks go unavailable based on in_stock.
const drinkCategoryIds = new Set([8, 9, 11, 12, 16]);

const ItemModal = ({ isOpen, item, onClose, onAdded, showToast, mode, cartItem }) => {
  const [selectedRequiredOption, setSelectedRequiredOption] = useState(mode === "edit" && cartItem?.selectedRequiredOption ? cartItem.selectedRequiredOption : null);
  const [selectedOptionalOptions, setSelectedOptionalOptions] = useState(mode === "edit" && cartItem?.selectedOptionalOptions ? [...cartItem.selectedOptionalOptions, ...(cartItem.item?.swallowOptions || [])] : []);
  const [selectedProteins, setSelectedProteins] = useState(mode === "edit" && cartItem?.item?.proteins ? cartItem.item.proteins : []);
  const [quantity, setQuantity] = useState(mode === "edit" && cartItem?.quantity ? cartItem.quantity : 1);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSwallowOptions, setShowSwallowOptions] = useState(false);
  const [showProteins, setShowProteins] = useState(false);
  const [showExtraProteins, setShowExtraProteins] = useState(false);

  const { addToCart, updateCartItem } = useCart();
  const swallowOptionIds = useMemo(() => [6, 7, 8, 9, 92, 93, 98], []);
  const noSwallowId = 98;

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !item) return;

    const swallows = (item.optional_options || []).filter((opt) => swallowOptionIds.includes(Number(opt.id)));
    const proteins = (item.optional_options || []).filter((opt) => !swallowOptionIds.includes(Number(opt.id)));
    const mainP = proteins.filter((opt) => mainProteinIds.includes(Number(opt.id)));
    const extraP = proteins.filter((opt) => !mainProteinIds.includes(Number(opt.id)));

    if (swallows.length > 0) {
      setShowSwallowOptions(true);
      setShowProteins(false);
      setShowExtraProteins(false);
    } else if (mainP.length > 0) {
      setShowSwallowOptions(false);
      setShowProteins(true);
      setShowExtraProteins(false);
    } else if (extraP.length > 0) {
      setShowSwallowOptions(false);
      setShowProteins(false);
      setShowExtraProteins(false);
    }
  }, [isOpen, item, swallowOptionIds]);

  const handleRequiredChange = (option) => setSelectedRequiredOption(option);
  const handleOptionalChange = (option) => {
    const isNoSwallow = Number(option.id) === noSwallowId;
    const exists = selectedOptionalOptions.find((opt) => Number(opt.id) === Number(option.id));
    
    if (exists) {
      setSelectedOptionalOptions((prev) => prev.filter((opt) => Number(opt.id) !== Number(option.id)));
    } else {
      if (isNoSwallow) {
        // If selecting "No Swallow", remove all other swallow options
        setSelectedOptionalOptions((prev) => [
          ...prev.filter((opt) => !swallowOptionIds.includes(Number(opt.id))),
          { ...option, quantity: 1 }
        ]);
      } else if (swallowOptionIds.includes(Number(option.id))) {
        // If selecting a swallow option, ensure "No Swallow" is removed
        setSelectedOptionalOptions((prev) => [
          ...prev.filter((opt) => Number(opt.id) !== noSwallowId),
          { ...option, quantity: 1 }
        ]);
      } else {
        setSelectedOptionalOptions((prev) => [...prev, { ...option, quantity: 1 }]);
      }
    }
  };
  const updateOptionalQuantity = (optionId, change) => {
    setSelectedOptionalOptions((prev) => prev.map((opt) => Number(opt.id) === Number(optionId) ? { ...opt, quantity: Math.max(1, opt.quantity + change) } : opt));
  };
  const handleProteinChange = (option) => {
    const exists = selectedProteins.find((opt) => opt.id === option.id);
    if (exists) { setSelectedProteins((prev) => prev.filter((opt) => opt.id !== option.id)); }
    else { setSelectedProteins((prev) => [...prev, { ...option, quantity: 1 }]); }
  };
  const updateProteinQuantity = (optionId, change) => {
    setSelectedProteins((prev) => prev.map((opt) => Number(opt.id) === Number(optionId) ? { ...opt, quantity: Math.max(1, opt.quantity + change) } : opt));
  };
  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => quantity > 1 && setQuantity(quantity - 1);

  const totalPrice = useMemo(() => {
    if (!item) return 0;
    let total = parseFloat(item.price) || 0;
    if (selectedRequiredOption) total += parseFloat(selectedRequiredOption.option_price || 0);
    selectedOptionalOptions.forEach((opt) => { total += parseFloat(opt.option_price || 0) * opt.quantity; });
    selectedProteins.forEach((opt) => { total += parseFloat(opt.option_price || 0) * opt.quantity; });
    return total * quantity;
  }, [item, selectedRequiredOption, selectedOptionalOptions, selectedProteins, quantity]);

  if (!isOpen || !item) return null;

  const swallowOptions = (item.optional_options || []).filter((opt) => swallowOptionIds.includes(Number(opt.id)));
  const sortedSwallowOptions = [...swallowOptions].sort((a, b) => { if (Number(a.id) === noSwallowId) return 1; if (Number(b.id) === noSwallowId) return -1; return 0; });
  const proteinOptions = (item.optional_options || []).filter((opt) => !swallowOptionIds.includes(Number(opt.id)));
  const mainProteins = proteinOptions.filter((opt) => mainProteinIds.includes(Number(opt.id)));
  const extraProteins = proteinOptions.filter((opt) => !mainProteinIds.includes(Number(opt.id)));
  const isNoSwallowSelected = selectedOptionalOptions.some((opt) => Number(opt.id) === noSwallowId);
  const isDrinkItem = drinkCategoryIds.has(Number(item.category_id));
  const isOutOfStock = isDrinkItem
    ? Number(item.in_stock) === 0
    : Number(item.manually_sold_out) === 1;
  const requiredOptions = item.required_options || [];

  const handleAddToCart = () => {
    if ((item.required_options?.length || 0) > 0 && !selectedRequiredOption) { setErrorMessage("Please select a required option."); return; }
    if (swallowOptions.length > 0 && selectedOptionalOptions.length === 0) { setErrorMessage("Please select a swallow option or choose No Swallow."); return; }
    const totalMainProteinQty = selectedProteins.filter((opt) => mainProteinIds.includes(Number(opt.id))).reduce((sum, opt) => sum + (opt.quantity || 0), 0);
    if (mainProteins.length > 0 && totalMainProteinQty < 2) { setErrorMessage("Please select 2 protein options."); return; }

    const mapOptions = (opts = []) => opts.map((opt) => ({ id: opt.id, option_name: opt.option_name, option_price: Number(opt.option_price || 0), quantity: Number(opt.quantity || 1) }));
    const mainProteinsSelected = selectedProteins.filter((opt) => mainProteinIds.includes(Number(opt.id)));
    const extraProteinsSelected = selectedProteins.filter((opt) => !mainProteinIds.includes(Number(opt.id)));

    const newItem = {
      item: { ...item, proteins: [...mainProteinsSelected, ...extraProteinsSelected], swallowOptions: mapOptions(selectedOptionalOptions.filter((opt) => swallowOptionIds.includes(Number(opt.id)))) },
      quantity,
      selectedRequiredOption: selectedRequiredOption ? { id: selectedRequiredOption.id, name: selectedRequiredOption.option_name, price: Number(selectedRequiredOption.option_price || 0) } : null,
      selectedOptionalOptions: mapOptions(selectedOptionalOptions.filter((opt) => !swallowOptionIds.includes(Number(opt.id)))),
    };

    if (mode === "edit" && cartItem) {
      newItem.key = cartItem.key; updateCartItem(cartItem.key, newItem); showToast && showToast("Item successfully updated");
    } else {
      newItem.key = `${item.id}-${Date.now()}-${Math.random()}`; addToCart(newItem); showToast && showToast("Item successfully added to basket");
    }
    onClose();
    onAdded && onAdded();
  };

  const getOptionDisplayPrice = (option, selected) => { const qty = selected ? selected.quantity : 1; return (parseFloat(option.option_price || 0) * qty).toFixed(2); };



  const totalMainProteinQty = selectedProteins.filter((opt) => mainProteinIds.includes(Number(opt.id))).reduce((sum, opt) => sum + (opt.quantity || 0), 0);

  const sectionHeadingCls = "bg-transparent text-white pt-2.5 pb-[10px] text-[20px] font-normal font-['Poppins',sans-serif] flex justify-between items-center";
  const optionLabelInlineCls = "flex flex-col gap-2.5 py-1.5 cursor-pointer text-white transition-colors duration-200 text-[18px] tracking-[1px]";
  const quantityBtnCls = "w-[30px] h-[30px] bg-brand-orange text-black border-none rounded-full cursor-pointer font-bold text-[22px] flex items-center justify-center select-none transition-colors duration-200 hover:bg-white hover:text-black disabled:bg-[#d3d3d3] disabled:text-[#a9a9a9] disabled:cursor-not-allowed";

  return createPortal(
    <>
      <div className="fixed inset-0 bg-[rgba(88,88,88,0.7)] z-[99999] flex justify-center items-center pointer-events-auto" onClick={onClose}>
        <div className="bg-black rounded-[10px] w-[92%] max-w-[530px] p-0 flex flex-col h-max max-h-[95vh] supports-[height:1dvh]:max-h-[95dvh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center gap-[14px] mb-5 shrink-0 relative px-[30px] pt-[15px]">
            <h2 className="text-white text-[28px] m-0 font-normal font-['Poppins',sans-serif]">{item.item_name}</h2>
            <i className="fas fa-times text-[26px] text-white cursor-pointer transition-colors duration-200 hover:text-brand-orange" onClick={onClose}></i>
          </div>

          <div className="flex-1 overflow-y-auto px-[30px] pb-[20px] [color-scheme:dark] [scrollbar-width:thin] [scrollbar-color:#fa9324_transparent] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#fa9324]">
            <img src={getMenuItemImage(item)} onError={handleMenuImageError} className="w-full h-[200px] object-cover mt-1.5 block" alt={item.item_name} />
            <ExpandableText text={item.item_description} className="mt-2.5 mb-0 text-[#eee] text-[16px] leading-relaxed" />

            {/* Required Options */}
            {requiredOptions.length > 0 && (
              <div className="mb-2.5 pt-2.5 bg-transparent border-none">
                <div className={sectionHeadingCls}><h4 className="m-0 font-normal font-['Poppins',sans-serif] text-[20px]">Required Options</h4></div>
                {requiredOptions.map((option) => {
                  const isSoldOut = Number(option.is_available) === 0 || option.is_available === false;
                  return (
                    <label key={option.id} className={`${optionLabelInlineCls} ${isSoldOut ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <div className={`flex items-center gap-3 w-full ${isSoldOut ? "pointer-events-none" : ""}`}>
                        <input type="radio" name="required-option" value={option.id} checked={selectedRequiredOption?.id === option.id} onChange={() => !isSoldOut && handleRequiredChange(option)} disabled={isSoldOut} className="w-5 h-5 accent-brand-orange cursor-pointer disabled:cursor-not-allowed" />
                        <span className={`flex-1 text-base ${isSoldOut ? "line-through text-gray-400" : ""}`}>{option.option_name} (£{parseFloat(option.option_price).toFixed(2)}) {isSoldOut && "(Sold Out)"}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Swallow Options */}
            {swallowOptions.length > 0 && (
              <div className="mb-2.5 pt-2.5 bg-transparent border-none">
                <div className={`${sectionHeadingCls} cursor-pointer`} onClick={() => { setShowSwallowOptions(!showSwallowOptions); if (!showSwallowOptions) { setShowProteins(false); setShowExtraProteins(false); } }}>
                  <h4 className="m-0 font-normal font-['Poppins',sans-serif] text-[19px] font-bold">Swallow Options</h4>
                  <span className={`inline-block text-white text-[12px] transition-transform duration-200 ${showSwallowOptions ? "rotate-0" : "-rotate-90"}`}>▼</span>
                </div>
                {showSwallowOptions && sortedSwallowOptions.map((option) => {
                  const isSoldOut = Number(option.is_available) === 0 || option.is_available === false;
                  const selected = selectedOptionalOptions.find((opt) => Number(opt.id) === Number(option.id));
                  const qty = selected ? selected.quantity : 1;
                  const isSwallowDisabled = isSoldOut || (isNoSwallowSelected && Number(option.id) !== noSwallowId);
                  return (
                    <label key={option.id} className={`flex flex-col gap-2 py-1 group ${isSoldOut ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <div className={`flex items-center gap-3 w-full ${isSwallowDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                        <div className="relative flex items-center justify-center">
                          <input 
                            type="checkbox" 
                            checked={!!selected} 
                            onChange={() => !isSoldOut && handleOptionalChange(option)} 
                            disabled={isSwallowDisabled} 
                            className="w-5 h-5 border-2 border-white bg-transparent appearance-none rounded-[4px] cursor-pointer checked:bg-brand-orange checked:border-brand-orange transition-all duration-200 disabled:cursor-not-allowed" 
                          />
                        </div>
                        <span className={`flex-1 text-[17px] text-white font-['Prata',serif] ${isSoldOut ? "line-through text-gray-400" : ""}`}>
                          {option.option_name} (£{getOptionDisplayPrice(option, selected)}) {isSoldOut && "(Sold Out)"}
                        </span>
                        {option.allow_quantity && selected && !isSoldOut && (
                          <div className="flex items-center gap-2 bg-transparent border-none w-fit ml-auto">
                            <button onClick={(e) => { e.preventDefault(); updateOptionalQuantity(option.id, -1); }} disabled={qty <= 1} className="w-[30px] h-[30px] rounded-full bg-[#717171] text-black border-none flex items-center justify-center font-bold text-[18px]">-</button>
                            <span className="font-bold text-[18px] min-w-[15px] text-center text-white">{qty}</span>
                            <button onClick={(e) => { e.preventDefault(); updateOptionalQuantity(option.id, 1); }} className="w-[30px] h-[30px] rounded-full bg-white text-black border-none flex items-center justify-center font-bold text-[18px]">+</button>
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Main Protein Options */}
            {mainProteins.length > 0 && (
              <div className="mb-2.5 pt-2.5 bg-transparent border-none">
                <div className={`${sectionHeadingCls} cursor-pointer`} onClick={() => { setShowProteins(!showProteins); if (!showProteins) { setShowSwallowOptions(false); setShowExtraProteins(false); } }}>
                  <h4 className="m-0 font-normal font-['Poppins',sans-serif] text-[19px] font-bold">Protein Options</h4>
                  <span className={`inline-block text-white text-[12px] transition-transform duration-200 ${showProteins ? "rotate-0" : "-rotate-90"}`}>▼</span>
                </div>
                {showProteins && mainProteins.map((option) => {
                  const isSoldOut = Number(option.is_available) === 0 || option.is_available === false;
                  const selected = selectedProteins.find((opt) => opt.id === option.id);
                  const qty = selected ? selected.quantity : 1;
                  const isProteinDisabled = isSoldOut || (totalMainProteinQty >= 2 && !selectedProteins.find((opt) => opt.id === option.id) && mainProteinIds.includes(Number(option.id)));
                  return (
                    <label key={option.id} className={`flex flex-col gap-2 py-1 group ${isSoldOut ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <div className={`flex items-center gap-3 w-full ${isProteinDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                        <div className="relative flex items-center justify-center">
                          <input 
                            type="checkbox" 
                            checked={!!selected} 
                            onChange={() => !isSoldOut && handleProteinChange(option)} 
                            disabled={isProteinDisabled} 
                            className="w-5 h-5 border-2 border-white bg-transparent appearance-none rounded-[4px] cursor-pointer checked:bg-brand-orange checked:border-brand-orange transition-all duration-200 disabled:cursor-not-allowed" 
                          />
                        </div>
                        <span className={`flex-1 text-[17px] text-white font-['Prata',serif] ${isSoldOut ? "line-through text-gray-400" : ""}`}>{option.option_name} (£{parseFloat(option.option_price).toFixed(2)}) {isSoldOut && "(Sold Out)"}</span>
                        {selected && option.allow_quantity && !isSoldOut && (
                          <div className="flex items-center gap-2 bg-transparent border-none w-fit ml-auto">
                            <button onClick={(e) => { e.preventDefault(); updateProteinQuantity(option.id, -1); }} disabled={qty <= 1} className="w-[30px] h-[30px] rounded-full bg-[#717171] text-black border-none flex items-center justify-center font-bold text-[18px]">-</button>
                            <span className="font-bold text-[18px] min-w-[15px] text-center text-white">{qty}</span>
                            <button onClick={(e) => { e.preventDefault(); updateProteinQuantity(option.id, 1); }} disabled={qty >= 2 || totalMainProteinQty >= 2} className="w-[30px] h-[30px] rounded-full bg-white text-black border-none flex items-center justify-center font-bold text-[18px]">+</button>
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Extra Protein Options */}
            {extraProteins.length > 0 && (
              <div className="mb-2.5 pt-2.5 bg-transparent border-none">
                <div className={`${sectionHeadingCls} cursor-pointer`} onClick={() => { setShowExtraProteins(!showExtraProteins); if (!showExtraProteins) { setShowSwallowOptions(false); setShowProteins(false); } }}>
                  <h4 className="m-0 font-normal font-['Poppins',sans-serif] text-[19px] font-bold">Extra</h4>
                  <span className={`inline-block text-white text-[12px] transition-transform duration-200 ${showExtraProteins ? "rotate-0" : "-rotate-90"}`}>▼</span>
                </div>
                {showExtraProteins && extraProteins.map((option) => {
                  const isSoldOut = Number(option.is_available) === 0 || option.is_available === false;
                  const selected = selectedProteins.find((opt) => opt.id === option.id);
                  const qty = selected ? selected.quantity : 1;
                  return (
                    <label key={option.id} className={`flex flex-col gap-2 py-1 group ${isSoldOut ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <div className={`flex items-center gap-3 w-full ${isSoldOut ? "opacity-40 pointer-events-none" : ""}`}>
                        <div className="relative flex items-center justify-center">
                          <input 
                            type="checkbox" 
                            checked={!!selected} 
                            onChange={() => !isSoldOut && handleProteinChange(option)} 
                            disabled={isSoldOut}
                            className="w-5 h-5 border-2 border-white bg-transparent appearance-none rounded-[4px] cursor-pointer checked:bg-brand-orange checked:border-brand-orange transition-all duration-200 disabled:cursor-not-allowed" 
                          />
                        </div>
                        <span className={`flex-1 text-[17px] text-white font-['Prata',serif] ${isSoldOut ? "line-through text-gray-400" : ""}`}>{option.option_name} (£{(parseFloat(option.option_price) * qty).toFixed(2)}) {isSoldOut && "(Sold Out)"}</span>
                        {selected && option.allow_quantity && !isSoldOut && (
                          <div className="flex items-center gap-2 bg-transparent border-none w-fit ml-auto">
                            <button onClick={(e) => { e.preventDefault(); updateProteinQuantity(option.id, -1); }} disabled={qty <= 1} className="w-[30px] h-[30px] rounded-full bg-[#717171] text-black border-none flex items-center justify-center font-bold text-[18px]">-</button>
                            <span className="font-bold text-[18px] min-w-[15px] text-center text-white">{qty}</span>
                            <button onClick={(e) => { e.preventDefault(); updateProteinQuantity(option.id, 1); }} className="w-[30px] h-[30px] rounded-full bg-white text-black border-none flex items-center justify-center font-bold text-[18px]">+</button>
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-4 mt-6 shrink-0 px-[30px] pb-[15px] flex-wrap">
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={decreaseQuantity} disabled={isOutOfStock} className={quantityBtnCls}>-</button>
              <span className={`font-bold text-[20px] min-w-[20px] text-center ${isOutOfStock ? "text-[#999]" : "text-white"}`}>{quantity}</span>
              <button onClick={increaseQuantity} disabled={isOutOfStock} className={quantityBtnCls}>+</button>
            </div>
            
            <button 
              onClick={handleAddToCart} 
              disabled={isOutOfStock} 
              className="bg-brand-orange text-black border-none px-5 py-3 rounded-[8px] font-bold text-[16px] cursor-pointer transition-all duration-300 hover:opacity-90 disabled:bg-[#d3d3d3] disabled:text-[#a9a9a9] disabled:cursor-not-allowed font-['Prata',serif] flex items-center justify-between w-[250px] min-h-[48px] max-w-full shadow-md flex-wrap gap-x-2"
            >
              {totalPrice === 0 && requiredOptions.length > 0 && !selectedRequiredOption ? (
                <span className="w-full text-center leading-tight">Add to Basket</span>
              ) : (
                <>
                  <span className="text-left leading-tight">Add to Basket</span>
                  <span className="font-bold shrink-0">£{totalPrice.toFixed(2)}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage("")} />}
    </>,
    document.body
  );
};

export default ItemModal;
